import api from "../../../api/axios";
import { messageFromError } from "../../../shared/api/drfErrors";

// Feature-local API wrappers for the createRealty surface (RIC-1).
//
// Per CLAUDE.md SR-6 (Local Duplication Is Preferred): createRealty does
// NOT import from features/editRealty. The nested upsert + feature
// wrappers are duplicated verbatim so the feature boundary stays clean;
// `createProperty` + `publishProperty` are the create-specific additions.
//
// Backend reference (backend/app/apps/realty/{urls,views,serializers}.py):
//
//   POST   /api/realty/properties/                     PropertyWriteSerializer
//                                                        → perform_create sets
//                                                          created_by + status=DRAFT
//   PUT    /api/realty/properties/{id}/location/        LocationSerializer
//   PUT    /api/realty/properties/{id}/residential/     ResidentialPropertySerializer
//   PUT    /api/realty/properties/{id}/land/            LandPropertySerializer
//   PUT    /api/realty/properties/{id}/commercial/      CommercialPropertySerializer
//   GET    /api/realty/features/                        Feature catalog [{id, name}]
//   POST   /api/realty/properties/{id}/features/        {feature_id} → 201 / 200
//   PATCH  /api/realty/properties/{id}/                 {status:'active'} (publish)
//
// RIC-3.1.2 — hold-then-upload: the wizard collects File objects locally
// and POSTs them only after the property id exists (see
// CreateRealtyScreenContent.runCreate). Multipart needs an explicit
// Content-Type override because the axios instance defaults to
// application/json.

const REALTY_BASE = "/realty";

export const createProperty = (body) =>
  api.post(`${REALTY_BASE}/properties/`, body).then((res) => res.data);

export const fetchPropertyDetail = (id) =>
  api.get(`${REALTY_BASE}/properties/${id}/`).then((res) => res.data);

export const patchPropertyCore = (id, body) =>
  api
    .patch(`${REALTY_BASE}/properties/${id}/`, body)
    .then((res) => res.data);

export const putPropertyLocation = (id, body) =>
  api
    .put(`${REALTY_BASE}/properties/${id}/location/`, body)
    .then((res) => res.data);

export const putPropertyResidential = (id, body) =>
  api
    .put(`${REALTY_BASE}/properties/${id}/residential/`, body)
    .then((res) => res.data);

export const putPropertyLand = (id, body) =>
  api
    .put(`${REALTY_BASE}/properties/${id}/land/`, body)
    .then((res) => res.data);

export const putPropertyCommercial = (id, body) =>
  api
    .put(`${REALTY_BASE}/properties/${id}/commercial/`, body)
    .then((res) => res.data);

export const fetchFeatureCatalog = () =>
  api.get(`${REALTY_BASE}/features/`).then((res) => res.data);

export const addPropertyFeature = (id, featureId) =>
  api
    .post(`${REALTY_BASE}/properties/${id}/features/`, {
      feature_id: featureId,
    })
    .then((res) => res.data);

export const removePropertyFeature = (id, featureId) =>
  api
    .delete(`${REALTY_BASE}/properties/${id}/features/${featureId}/`)
    .then((res) => res.data);

// Publish — PATCH status=active. Backend Property.clean() requires
// location + active subtype rows before leaving DRAFT (no image required).
export const publishProperty = (id) =>
  api
    .patch(`${REALTY_BASE}/properties/${id}/`, { status: "active" })
    .then((res) => res.data);

// RIC-3.1.2 — upload collected files after the property exists. Backend
// `PropertyImageUploadSerializer` expects repeated `images` parts + `kind`
// ("image" | "plan"). axios detects the FormData body and sets
// `multipart/form-data` with the correct boundary automatically — do not
// set Content-Type manually (a hand-set header omits the boundary).
export function propertyImageUploadErrorMessage(error) {
  if (error && error.code === "PROPERTY_UPLOAD_ID_MISSING" && error.message) {
    return error.message;
  }
  const data = error && error.response && error.response.data;
  if (data && typeof data === "object") {
    if (typeof data.images === "string" && data.images.trim()) {
      return data.images.trim();
    }
    if (
      Array.isArray(data.images)
      && typeof data.images[0] === "string"
      && data.images[0].trim()
    ) {
      return data.images[0].trim();
    }
  }
  return messageFromError(error);
}

function assertPropertyUploadId(id) {
  const propertyId = Number(id);
  if (!Number.isInteger(propertyId) || propertyId <= 0) {
    const err = new Error(
      "Об'єкт ще не створено. Завантаження зображень недоступне.",
    );
    err.code = "PROPERTY_UPLOAD_ID_MISSING";
    return Promise.reject(err);
  }
  return Promise.resolve(propertyId);
}

export const uploadPropertyImages = (id, files, kind = "image") => {
  return assertPropertyUploadId(id).then((propertyId) => {
    const fd = new FormData();
    for (const file of files) fd.append("images", file);
    fd.append("kind", kind);
    return api
      .post(`${REALTY_BASE}/properties/${propertyId}/images/`, fd)
      .then((res) => {
        const data = res.data;
        if (Array.isArray(data)) {
          return { images: data, errors: [] };
        }
        return {
          images: data.images || [],
          errors: data.errors || [],
        };
      });
  });
};

export const patchPropertyImage = (id, imageId, body) => {
  const payload = body && body.image instanceof File
    ? (() => {
      const form = new FormData();
      form.append("image", body.image);
      return form;
    })()
    : body;
  return api
    .patch(`${REALTY_BASE}/properties/${id}/images/${imageId}/`, payload)
    .then((res) => res.data);
};

export const deletePropertyImage = (id, imageId) =>
  api
    .delete(`${REALTY_BASE}/properties/${id}/images/${imageId}/`)
    .then((res) => res.data);

export const reorderPropertyImages = (id, kind, imageIds) =>
  assertPropertyUploadId(id).then((propertyId) =>
    api
      .put(`${REALTY_BASE}/properties/${propertyId}/images/reorder/`, {
        kind,
        image_ids: imageIds,
      })
      .then((res) => res.data),
  );
